import { curveMonotoneX, max as d3Max, extent, line, scaleLinear, scaleTime } from 'd3'
import { format } from 'date-fns'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CARE_RX_COLOR, careColor } from '@/lib/care-palette'
import { cn } from '@/lib/utils'
import type { CareSeries, CareSeriesPoint, CareTimeline } from '@/types/care'

/** `sections` plots every series against its own maximum; `total` plots the questionnaire score alone. */
export type CareTimelineMode = 'sections' | 'total'

interface CareTimelineChartProps {
  timeline: CareTimeline
  mode: CareTimelineMode
  height?: number
  className?: string
}

const MARGIN = { top: 20, right: 32, bottom: 44, left: 56 }
const DAY_MS = 24 * 60 * 60 * 1000
const TOOLTIP_HALF_WIDTH = 96
/** Beyond this many check-ins the axis labels collide, so they are thinned. */
const MAX_AXIS_LABELS = 8

/** A "nice" axis step (1/2/5 × 10ⁿ) giving roughly four gridlines up to `maxValue`. */
const niceStep = (maxValue: number): number => {
  if (maxValue <= 0) return 1
  const rough = maxValue / 4
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  const nice = normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10
  return nice * magnitude
}

export default function CareTimelineChart({ timeline, mode, height = 380, className }: CareTimelineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(720)
  /** Series the prescriber has switched off. Keyed by series key, so it survives a re-order. */
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [hoveredDate, setHoveredDate] = useState<number | null>(null)

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return
    // Measure before paint so the first frame is not drawn at the placeholder width.
    setWidth(element.getBoundingClientRect().width || 720)
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.width
      if (next) setWidth(next)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // `total` draws the questionnaire score alone; `sections` draws it alongside the sections, where
  // the shared percentage axis is what makes them comparable.
  const allSeries = useMemo<CareSeries[]>(() => {
    if (mode === 'total') return timeline.total ? [timeline.total] : []
    return [...timeline.series, ...(timeline.total ? [timeline.total] : [])]
  }, [timeline, mode])

  const visible = allSeries.filter(series => !hidden.has(series.key))

  const toggle = (key: string) =>
    setHidden(previous => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  /**
   * A series' value on the shared axis. In `sections` the axis is "% of highest possible score",
   * so a series with no known maximum has no place on it and is left undrawn rather than plotted
   * against someone else's scale.
   */
  const yValue = (point: CareSeriesPoint) => {
    if (mode === 'total') return point.value
    return point.ratio === null ? null : point.ratio * 100
  }

  const { dates } = timeline
  if (dates.length === 0 || allSeries.length === 0) return null

  // X scale — pad a single-date history so the domain is never degenerate.
  const [minDate, maxDate] = extent(dates) as [Date, Date]
  const flat = minDate.getTime() === maxDate.getTime()
  const domainStart = flat ? new Date(minDate.getTime() - DAY_MS) : minDate
  const domainEnd = flat ? new Date(maxDate.getTime() + DAY_MS) : maxDate
  const xScale = scaleTime()
    .domain([domainStart, domainEnd])
    .range([MARGIN.left, width - MARGIN.right])

  // Y scale. The percentage axis is fixed at 0–100 so the shape of a series is the same whichever
  // sections are switched on; the total axis is anchored to the questionnaire's own maximum, which
  // is what "out of the highest possible score" means.
  const dataMax = d3Max(visible.flatMap(series => series.points.map(yValue).filter(v => v !== null) as number[])) ?? 0
  const totalMax = timeline.total?.max ?? null
  const yMax =
    mode === 'sections'
      ? 100
      : totalMax && totalMax > 0
        ? totalMax
        : Math.max(niceStep(dataMax), Math.ceil(dataMax / niceStep(dataMax)) * niceStep(dataMax))
  const step = mode === 'sections' ? 25 : niceStep(yMax)
  const yScale = scaleLinear()
    .domain([0, yMax])
    .range([height - MARGIN.bottom, MARGIN.top])

  const yTicks: number[] = []
  for (let tick = 0; tick <= yMax + 1e-9; tick += step) yTicks.push(tick)
  // A step that does not divide the maximum leaves the axis topping out below it — label the
  // maximum itself, unless the last step already lands close enough to collide with it.
  const lastTick = yTicks.at(-1) ?? 0
  if (yMax - lastTick > step / 4) yTicks.push(yMax)

  const formatY = (tick: number) => (mode === 'sections' ? `${tick}%` : `${tick}`)

  // Axis labels sit on the check-in dates themselves — a care series is a handful of appointments,
  // not a continuous signal, so calendar ticks would label gaps rather than measurements.
  const thin = Math.ceil(dates.length / MAX_AXIS_LABELS)
  const rxDates = new Set(timeline.rxEvents.map(event => event.date.getTime()))
  const axisDates = dates.filter((date, index) => index % thin === 0 || rxDates.has(date.getTime()))
  const spansMultipleYears = new Set(dates.map(date => date.getFullYear())).size > 1

  const linePath = (series: CareSeries) =>
    line<CareSeriesPoint>()
      .defined(point => yValue(point) !== null)
      .x(point => xScale(point.date))
      .y(point => yScale(yValue(point) as number))
      .curve(curveMonotoneX)(series.points) ?? ''

  const hoveredValues = hoveredDate
    ? visible
        .map(series => {
          const point = series.points.find(candidate => candidate.date.getTime() === hoveredDate)
          const value = point ? yValue(point) : null
          return value === null || !point ? null : { series, point, value }
        })
        .filter(entry => entry !== null)
    : []
  const tooltipLeft = hoveredDate
    ? Math.max(TOOLTIP_HALF_WIDTH, Math.min(width - TOOLTIP_HALF_WIDTH, xScale(new Date(hoveredDate))))
    : 0

  return (
    <div className={cn('w-full font-poppins', className)}>
      <div ref={containerRef} className='relative w-full'>
        <svg
          width={width}
          height={height}
          role='img'
          aria-label={
            mode === 'sections'
              ? 'Symptom scores over time, as a percentage of the highest possible score'
              : 'Questionnaire score over time'
          }
          className='overflow-visible'
        >
          {/* Horizontal gridlines + Y tick labels */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke='currentColor'
                strokeDasharray='4 4'
                className='text-custom-neutral-200'
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 12}
                y={yScale(tick)}
                textAnchor='end'
                dominantBaseline='middle'
                className='fill-custom-neutral-400 text-xs'
              >
                {formatY(tick)}
              </text>
            </g>
          ))}

          {/* Prescription dividers — dotted for a change of medication, solid for a renewal */}
          {timeline.rxEvents.map(event => (
            <line
              key={`${event.kind}-${event.date.getTime()}`}
              x1={xScale(event.date)}
              x2={xScale(event.date)}
              y1={MARGIN.top - 8}
              y2={height - MARGIN.bottom}
              stroke={CARE_RX_COLOR}
              strokeWidth={1.5}
              strokeDasharray={event.kind === 'change' ? '5 5' : undefined}
            >
              <title>{`${event.label} — ${format(event.date, 'd MMM yyyy')}`}</title>
            </line>
          ))}

          {/* X axis labels. A date carrying a prescription event is marked in the divider's colour. */}
          {axisDates.map(date => {
            const isRx = rxDates.has(date.getTime())
            return (
              <text
                key={date.getTime()}
                x={Math.max(MARGIN.left, Math.min(width - MARGIN.right, xScale(date)))}
                y={height - MARGIN.bottom + 24}
                textAnchor='middle'
                className={cn('text-xs', isRx ? 'font-semibold' : 'fill-custom-neutral-400')}
                fill={isRx ? CARE_RX_COLOR : undefined}
              >
                {format(date, spansMultipleYears ? 'd MMM yy' : 'd MMM')}
              </text>
            )
          })}

          {/* Hover guide for the date column under the cursor */}
          {hoveredDate !== null && (
            <line
              x1={xScale(new Date(hoveredDate))}
              x2={xScale(new Date(hoveredDate))}
              y1={MARGIN.top}
              y2={height - MARGIN.bottom}
              stroke='currentColor'
              className='text-custom-neutral-400'
              strokeWidth={1}
            />
          )}

          {/* Trend lines + markers */}
          {visible.map(series => (
            <g key={series.key}>
              <path
                d={linePath(series)}
                fill='none'
                stroke={careColor(series.color)}
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              {series.points.map(point => {
                const value = yValue(point)
                if (value === null) return null
                return (
                  <circle
                    key={`${series.key}-${point.date.getTime()}`}
                    cx={xScale(point.date)}
                    cy={yScale(value)}
                    r={hoveredDate === point.date.getTime() ? 6 : 4.5}
                    fill={careColor(series.color)}
                    stroke='white'
                    strokeWidth={1.5}
                  />
                )
              })}
            </g>
          ))}

          {/* One transparent column per check-in, so hovering anywhere in it reads that date */}
          {dates.map(date => {
            const columnWidth = Math.max(16, (width - MARGIN.left - MARGIN.right) / Math.max(dates.length, 1))
            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: hover affordance only — the same values are in the sr-only summary below
              <rect
                key={`hit-${date.getTime()}`}
                x={xScale(date) - columnWidth / 2}
                y={MARGIN.top}
                width={columnWidth}
                height={height - MARGIN.bottom - MARGIN.top}
                fill='transparent'
                onMouseEnter={() => setHoveredDate(date.getTime())}
                onMouseLeave={() => setHoveredDate(null)}
              />
            )
          })}
        </svg>

        {hoveredDate !== null && hoveredValues.length > 0 && (
          <div
            className='pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-custom-neutral-900 px-3 py-2 shadow-lg'
            style={{ left: tooltipLeft, top: MARGIN.top }}
          >
            <p className='mb-1 whitespace-nowrap text-xs text-white/70'>
              {format(new Date(hoveredDate), 'd MMM yyyy')}
            </p>
            {hoveredValues.map(entry => (
              <p key={entry.series.key} className='flex items-center gap-2 whitespace-nowrap text-sm text-white'>
                <span
                  className='inline-block h-2 w-2 shrink-0 rounded-full'
                  style={{ backgroundColor: careColor(entry.series.color) }}
                />
                <span className='flex-1'>{entry.series.name}</span>
                <span className='font-semibold'>
                  {mode === 'sections'
                    ? `${Math.round(entry.value)}%`
                    : `${entry.point.value}${entry.series.max ? `/${entry.series.max}` : ''}`}
                </span>
              </p>
            ))}
          </div>
        )}

        {/* Text alternative — the SVG itself carries only a label. */}
        <span className='sr-only'>
          {visible
            .map(
              series =>
                `${series.name}: ${series.points
                  .filter(point => yValue(point) !== null)
                  .map(
                    point =>
                      `${mode === 'sections' ? `${Math.round(yValue(point) as number)}%` : point.value} on ${format(point.date, 'd MMM yyyy')}`
                  )
                  .join(', ')}`
            )
            .join('. ')}
        </span>
      </div>

      {/* Legend — each entry toggles its series */}
      <div className='mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-[#FAFAFB] px-5 py-3'>
        {allSeries.map(series => {
          const isHidden = hidden.has(series.key)
          return (
            <button
              key={series.key}
              type='button'
              role='switch'
              aria-checked={!isHidden}
              onClick={() => toggle(series.key)}
              className='flex cursor-pointer items-center gap-2 text-sm transition-opacity'
              style={{ opacity: isHidden ? 0.4 : 1 }}
            >
              <span
                className='grid h-5 w-8 place-items-center rounded-full'
                style={{ backgroundColor: `${careColor(series.color)}33` }}
              >
                <span
                  className='h-2.5 w-2.5 rounded-full transition-transform'
                  style={{
                    backgroundColor: careColor(series.color),
                    transform: isHidden ? 'translateX(-5px)' : 'translateX(5px)'
                  }}
                />
              </span>
              <span style={{ color: careColor(series.color) }} className='font-medium'>
                {series.name}
              </span>
            </button>
          )
        })}

        <span className='ml-auto flex items-center gap-4 text-xs' style={{ color: CARE_RX_COLOR }}>
          <span className='flex items-center gap-2'>
            <svg width='22' height='2' aria-hidden='true'>
              <line x1='0' y1='1' x2='22' y2='1' stroke={CARE_RX_COLOR} strokeWidth='2' />
            </svg>
            Rx
          </span>
          <span className='flex items-center gap-2'>
            <svg width='22' height='2' aria-hidden='true'>
              <line x1='0' y1='1' x2='22' y2='1' stroke={CARE_RX_COLOR} strokeWidth='2' strokeDasharray='4 3' />
            </svg>
            Rx change
          </span>
        </span>
      </div>
    </div>
  )
}
